import { useState, type FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/danqbkt1jbh5ebt7feo0';

const contactInfo = [
  { icon: 'ri-mail-line', label: '品牌合作', value: 'info@mizean.com' },
  { icon: 'ri-map-pin-2-line', label: '创作基地', value: '杭州 · 中国' },
];

export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('contact_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }
    formData.delete('contact_alt');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === 'string' && value.trim() !== '') {
        payload.append(key, value);
      }
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: unknown = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const data = parsed as
        | { code?: string; message?: string; meta?: { message?: string; detail?: string } }
        | null;

      const serverMsg =
        data?.meta?.message || data?.message || data?.meta?.detail || responseText;
      const code = data?.code;
      const isSpam =
        typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        setFormError('');
        form.reset();
      } else {
        setStatus('error');
        setFormError(
          typeof serverMsg === 'string' && serverMsg.trim()
            ? serverMsg
            : '提交失败，请稍后再试。',
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，请稍后再试。');
    }
  };

  return (
    <section id="contact" className="bg-background-100 py-16 md:py-28">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="reveal lg:col-span-5">
            <div className="mb-6 flex items-center gap-3">
              <span className="text-[11px] tracking-[0.34em] text-primary-600 uppercase">
                Contact
              </span>
              <span className="h-px w-8 bg-primary-300"></span>
              <span className="text-[11px] tracking-[0.28em] text-foreground-400 uppercase">
                Let&apos;s Create Together
              </span>
            </div>
            <h2 className="font-heading text-3xl leading-snug text-foreground-950 md:text-4xl">
              把东方的一点美好，
              <br />
              带回生活
            </h2>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-foreground-600 md:text-base">
              无论你想做品牌联名、城市共创、内容合作，还是想把一个地方的味道变成一个角色，
              都欢迎和我们聊聊。
            </p>

            <div className="mt-9 space-y-5">
              {contactInfo.map((item) => (
                <div key={item.label} className="flex items-center gap-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-background-50 text-primary-600">
                    <i className={`${item.icon} text-lg`}></i>
                  </span>
                  <div>
                    <p className="text-[11px] tracking-[0.18em] text-foreground-400 uppercase">
                      {item.label}
                    </p>
                    <p className="mt-0.5 text-sm text-foreground-900">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="reveal lg:col-span-7" style={{ transitionDelay: '140ms' }}>
            <form
              id="nomoism-contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="rounded-lg border border-background-200/70 bg-background-50 p-6 md:p-8"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="nomo-name"
                    className="mb-2 block text-xs tracking-[0.12em] text-foreground-600"
                  >
                    姓名
                  </label>
                  <input
                    id="nomo-name"
                    name="name"
                    type="text"
                    required
                    placeholder="你的名字"
                    className="w-full rounded-md border border-background-300/70 bg-background-100 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors duration-200 placeholder:text-foreground-400 focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
                  />
                </div>
                <div>
                  <label
                    htmlFor="nomo-email"
                    className="mb-2 block text-xs tracking-[0.12em] text-foreground-600"
                  >
                    邮箱
                  </label>
                  <input
                    id="nomo-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="w-full rounded-md border border-background-300/70 bg-background-100 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors duration-200 placeholder:text-foreground-400 focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
                  />
                </div>
                <div>
                  <label
                    htmlFor="nomo-company"
                    className="mb-2 block text-xs tracking-[0.12em] text-foreground-600"
                  >
                    机构 / 品牌
                  </label>
                  <input
                    id="nomo-company"
                    name="company"
                    type="text"
                    placeholder="选填"
                    className="w-full rounded-md border border-background-300/70 bg-background-100 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors duration-200 placeholder:text-foreground-400 focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
                  />
                </div>
                <div>
                  <label
                    htmlFor="nomo-type"
                    className="mb-2 block text-xs tracking-[0.12em] text-foreground-600"
                  >
                    合作类型
                  </label>
                  <select
                    id="nomo-type"
                    name="cooperation_type"
                    defaultValue=""
                    className="w-full rounded-md border border-background-300/70 bg-background-100 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors duration-200 focus:border-primary-400 focus:ring-1 focus:ring-primary-300 cursor-pointer"
                  >
                    <option value="" disabled>
                      请选择
                    </option>
                    <option value="品牌联名">品牌联名</option>
                    <option value="城市共创">城市共创</option>
                    <option value="渠道 / 经销">渠道 / 经销</option>
                    <option value="媒体 / 内容">媒体 / 内容</option>
                    <option value="其他合作">其他合作</option>
                  </select>
                </div>
              </div>

              <div className="mt-5">
                <label
                  htmlFor="nomo-message"
                  className="mb-2 block text-xs tracking-[0.12em] text-foreground-600"
                >
                  想聊点什么
                </label>
                <textarea
                  id="nomo-message"
                  name="message"
                  rows={5}
                  maxLength={500}
                  placeholder="简单说说你的想法（最多 500 字）"
                  className="w-full resize-none rounded-md border border-background-300/70 bg-background-100 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors duration-200 placeholder:text-foreground-400 focus:border-primary-400 focus:ring-1 focus:ring-primary-300"
                ></textarea>
              </div>

              <input
                className="nomo-aux-field"
                type="text"
                name="contact_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-foreground-400">
                  我们通常会在 3 个工作日内回复你。
                </p>
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-8 py-3.5 text-sm text-background-50 transition-colors duration-300 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer whitespace-nowrap"
                >
                  {status === 'submitting' ? '提交中…' : '发送合作意向'}
                  {status !== 'submitting' && <i className="ri-send-plane-line"></i>}
                </button>
              </div>

              {status === 'success' && (
                <div className="mt-5 flex items-center gap-3 rounded-md bg-secondary-50 px-4 py-3 text-sm text-secondary-800">
                  <i className="ri-checkbox-circle-line text-lg"></i>
                  已收到你的信息，谢谢！我们会尽快与你联系。
                </div>
              )}
              {status === 'error' && (
                <div className="mt-5 flex items-center gap-3 rounded-md bg-primary-50 px-4 py-3 text-sm text-primary-700">
                  <i className="ri-error-warning-line text-lg"></i>
                  {formError || '提交失败，请稍后再试。'}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}